import { SportType } from "../data/activities/types";
import dayjs from "../lib/dayjs";

type SportConfig = {
  name: string;
  id: SportType;
  emoji: string;
  requiresDistance: boolean;
  requiresElevation: boolean;
  color: string; // HEX color for chart visualizations
};

export const sportConfigs: SportConfig[] = [
  {
    name: "Walking",
    id: "walking",
    emoji: "🚶",
    requiresDistance: true,
    requiresElevation: true,
    color: "#a855f7", // purple-500
  },
  {
    name: "Running",
    id: "running",
    emoji: "🏃",
    requiresDistance: true,
    requiresElevation: true,
    color: "#3b82f6", // blue-500
  },
  {
    name: "Biking",
    id: "biking",
    emoji: "🚴‍♂️",
    requiresDistance: true,
    requiresElevation: true,
    color: "#f97316", // orange-500
  },
  {
    name: "E-Biking",
    id: "ebiking",
    emoji: "🚴",
    requiresDistance: true,
    requiresElevation: true,
    color: "#f7dc6f", // amber-500
  },
  {
    name: "Swimming",
    id: "swimming",
    emoji: "🏊",
    requiresDistance: true,
    requiresElevation: false,
    color: "#2dd4bf", // teal-400
  },
  {
    name: "Badminton",
    id: "badminton",
    emoji: "🏸",
    requiresDistance: false,
    requiresElevation: false,
    color: "#8b9467", // sage-500
  },
  {
    name: "Basketball",
    id: "basketball",
    emoji: "🏀",
    requiresDistance: false,
    requiresElevation: false,
    color: "#f2c464", // yellow-500
  },
  {
    name: "Boxing",
    id: "boxing",
    emoji: "🥊",
    requiresDistance: false,
    requiresElevation: false,
    color: "#ef4444", // red-500
  },
  {
    name: "Golf",
    id: "golfing",
    emoji: "🏌️",
    requiresDistance: false,
    requiresElevation: false,
    color: "#22c55e", // green-500
  },
  {
    name: "Hockey",
    id: "hockey",
    emoji: "🏑",
    requiresDistance: false,
    requiresElevation: false,
    color: "#4f46e5", // indigo-500
  },
  {
    name: "Ice Hockey",
    id: "ice_hockey",
    emoji: "🏒",
    requiresDistance: false,
    requiresElevation: false,
    color: "#6366f1", // blue-600
  },
  {
    name: "Horseback Riding",
    id: "horseback_riding",
    emoji: "🏇",
    requiresDistance: false,
    requiresElevation: false,
    color: "#8b5cf6", // purple-600
  },
  {
    name: "Martial Arts",
    id: "martial_arts",
    emoji: "🥋",
    requiresDistance: false,
    requiresElevation: false,
    color: "#f59e0b", // orange-600
  },
  {
    name: "Paddle",
    id: "paddle",
    emoji: "🎾",
    requiresDistance: false,
    requiresElevation: false,
    color: "#10b981", // teal-500
  },
  {
    name: "Soccer",
    id: "soccer",
    emoji: "⚽",
    requiresDistance: false,
    requiresElevation: false,
    color: "#14b8a6", // teal-600
  },
  {
    name: "Tennis",
    id: "tennis",
    emoji: "🎾",
    requiresDistance: false,
    requiresElevation: false,
    color: "#06b6d4", // cyan-500
  },
  {
    name: "Yoga",
    id: "yoga",
    emoji: "🧘",
    requiresDistance: false,
    requiresElevation: false,
    color: "#8d70fe", // purple-700
  },
  {
    name: "Cardio",
    id: "cardio",
    emoji: "🫀",
    requiresDistance: false,
    requiresElevation: false,
    color: "#c026d3", // pink-500
  },
  {
    name: "Calisthenics",
    id: "calisthenics",
    emoji: "💪",
    requiresDistance: false,
    requiresElevation: false,
    color: "#9333ea", // purple-800
  },
  {
    name: "Weightlifting",
    id: "weightlifting",
    emoji: "🏋️",
    requiresDistance: false,
    requiresElevation: false,
    color: "#a811b1", // purple-500
  },
  {
    name: "Rowing",
    id: "rowing",
    emoji: "🚣",
    requiresDistance: false,
    requiresElevation: false,
    color: "#2563eb", // blue-700
  },
  {
    name: "Pilates",
    id: "pilates",
    emoji: "🤸",
    requiresDistance: false,
    requiresElevation: false,
    color: "#f7d2c4", // orange-200
  },
  {
    name: "Zumba",
    id: "zumba",
    emoji: "🕺",
    requiresDistance: false,
    requiresElevation: false,
    color: "#f2c464", // yellow-500
  },
  {
    name: "Dancing",
    id: "dancing",
    emoji: "💃",
    requiresDistance: false,
    requiresElevation: false,
    color: "#f59e0b", // orange-600
  },
  {
    name: "Rugby",
    id: "rugby",
    emoji: "🏉",
    requiresDistance: false,
    requiresElevation: false,
    color: "#E55137",
  },
];

export const getSportConfigByType = (type: string): SportConfig => {
  return (
    sportConfigs.find((config) => config.id === type) || {
      name: "Other",
      id: "other",
      emoji: "🏅",
      requiresDistance: false,
      requiresElevation: false,
      color: "#64748b", // gray-500
    }
  );
};

/**
 * Get the emoji for a sport type using the sportConfigs
 * @param type The sport type to get the emoji for
 * @returns The emoji for the sport type, or a default emoji if not found
 */
export const getEmojiBySportType = (type: string): string => {
  const sportConfig = sportConfigs.find((config) => config.id === type);
  return sportConfig?.emoji || "🏅";
};

export type PaceUnit = "KM/H" | "MIN/KM" | "MIN/100M";

export const getPaceFormatBySportType = (type: string): PaceUnit => {
  if (type === "biking" || type === "ebiking") {
    return "KM/H";
  }

  if (type === "running") {
    return "MIN/KM";
  }

  if (type === "swimming") {
    return "MIN/100M";
  }

  return "MIN/KM";
};

export const formatPace = (
  durationInSeconds: number,
  distanceInKm: number,
  sportType: string
) => {
  if (durationInSeconds === 0 || distanceInKm === 0) {
    return "00:00";
  }

  const unit = getPaceFormatBySportType(sportType);

  if (unit === "KM/H") {
    const speedInKmPerHour = distanceInKm / (durationInSeconds / 3600);
    return Math.round(speedInKmPerHour);
  }

  let paceInSecondsPerUnit = 0;
  if (unit === "MIN/KM") {
    paceInSecondsPerUnit = durationInSeconds / distanceInKm;
  } else if (unit === "MIN/100M") {
    const distanceInMeters = distanceInKm * 1000;
    paceInSecondsPerUnit = (durationInSeconds / distanceInMeters) * 100;
  }

  const paceDuration = dayjs.duration(paceInSecondsPerUnit, "seconds");
  const minutes = String(paceDuration.minutes()).padStart(2, "0");
  const seconds = String(paceDuration.seconds()).padStart(2, "0");

  return `${minutes}:${seconds}`;
};
